/**
 * Emergent Marketplace — Phase 3
 * VendorOrders.jsx
 * Architectural Layer: Presentation → Vendor
 *
 * Vendor's view of orders. Each order shows only their split.
 * Mark shipped, add tracking numbers, view customer details.
 * Tabs: Unfulfilled (priority) · Shipped · All
 * Design: Clean dispatch — warm white, deep teal, status-coded accents.
 * Font: Syne (display) + IBM Plex Sans (body)
 */

import { useState, useEffect } from "react";
import {
  Package, Truck, CheckCircle, Clock, Search,
  ChevronDown, ChevronUp, ExternalLink, X, Check,
  AlertCircle, Download, BookOpen, Calendar, Loader
} from "lucide-react";
import { apiFetch } from "./api";
import { Pagination } from "../../components/Pagination";

// ─── Styles ──────────────────────────────────────────────────────────────────
const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700&family=IBM+Plex+Sans:wght@300;400;500;600&display=swap');

  .vo-root {
    --teal:       #1a4a3a;
    --teal-mid:   #2d6b54;
    --teal-light: #4a8a70;
    --teal-bg:    #eaf5f0;
    --teal-border:#b0d8c8;
    --white:      #ffffff;
    --off-white:  #f8f6f2;
    --border:     #e4ddd4;
    --ink:        #1a1a18;
    --ink-mid:    #5a5a54;
    --ink-light:  #9a9a90;
    --amber:      #a06010;
    --amber-bg:   #fef8ea;
    --amber-border:#e8d080;
    --blue:       #1a50a0;
    --blue-bg:    #eef4fd;
    --blue-border:#b0cce8;
    --green:      #1a6a40;
    --green-bg:   #eaf5f0;
    --red:        #a02828;
    --red-bg:     #fdf0f0;
    font-family: 'IBM Plex Sans', sans-serif;
    background: var(--off-white);
    color: var(--ink);
    min-height: 100vh;
  }

  .vo-header {
    background: var(--white);
    border-bottom: 1px solid var(--border);
    padding: 18px 28px;
    display: flex;
    gap: 16px;
    align-items: center;
    position: sticky;
    top: 0;
    z-index: 20;
  }

  .vo-header h1 {
    font-family: 'Syne', sans-serif;
    font-size: 20px;
    font-weight: 700;
    color: var(--teal);
    margin: 0;
    flex-shrink: 0;
  }

  .vo-search {
    flex: 1;
    display: flex;
    align-items: center;
    gap: 8px;
    background: var(--off-white);
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 8px 14px;
  }

  .vo-search input {
    background: none; border: none; outline: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px; color: var(--ink); width: 100%;
  }

  .vo-search input::placeholder { color: var(--ink-light); }

  /* Tabs */
  .vo-tabs {
    display: flex;
    gap: 0;
    padding: 0 28px;
    background: var(--white);
    border-bottom: 1px solid var(--border);
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
  }

  .vo-tab {
    padding: 11px 16px;
    border: none; background: none;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px; font-weight: 500;
    color: var(--ink-light);
    cursor: pointer;
    border-bottom: 2px solid transparent;
    transition: all 0.15s;
    display: flex; align-items: center; gap: 8px;
    white-space: nowrap;
    flex-shrink: 0;
  }

  .vo-tab.active { color: var(--teal); border-bottom-color: var(--teal); font-weight: 600; }

  .vo-tab-badge {
    background: var(--amber-bg);
    color: var(--amber);
    border: 1px solid var(--amber-border);
    border-radius: 10px;
    padding: 1px 7px;
    font-size: 10px;
    font-weight: 700;
  }

  .vo-tab.active .vo-tab-badge {
    background: var(--teal);
    color: white;
    border-color: transparent;
  }

  /* Order list */
  .vo-list { padding: 16px 28px 40px; display: flex; flex-direction: column; gap: 10px; }

  .vo-order-card {
    background: var(--white);
    border: 1px solid var(--border);
    border-radius: 12px;
    overflow: hidden;
    transition: box-shadow 0.15s;
  }

  .vo-order-card:hover { box-shadow: 0 3px 14px rgba(0,0,0,0.06); }

  /* Card header row */
  .vo-order-head {
    display: flex;
    align-items: center;
    gap: 14px;
    padding: 14px 18px;
    cursor: pointer;
    user-select: none;
  }

  .vo-order-ref {
    font-family: 'Syne', sans-serif;
    font-size: 14px;
    font-weight: 700;
    color: var(--teal);
    flex-shrink: 0;
    width: 80px;
  }

  .vo-order-customer { font-size: 14px; font-weight: 500; color: var(--ink); flex: 1; }
  .vo-order-date     { font-size: 12px; color: var(--ink-light); white-space: nowrap; }
  .vo-order-amount   { font-size: 15px; font-weight: 700; color: var(--ink); white-space: nowrap; }

  .vo-order-fulfillment {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 10px;
    border-radius: 10px;
    font-size: 11px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    white-space: nowrap;
  }

  .vo-fulfillment-unfulfilled { background: var(--amber-bg); color: var(--amber); border: 1px solid var(--amber-border); }
  .vo-fulfillment-shipped     { background: var(--blue-bg);  color: var(--blue);  border: 1px solid var(--blue-border); }
  .vo-fulfillment-delivered   { background: var(--green-bg); color: var(--green); border: 1px solid var(--teal-border); }

  .vo-expand-btn {
    background: none; border: none; cursor: pointer;
    color: var(--ink-light); padding: 4px; display: flex;
  }

  /* Expanded detail */
  .vo-order-detail {
    border-top: 1px solid var(--off-white);
    background: #fafaf8;
    animation: vo-expand 0.18s ease;
  }

  @keyframes vo-expand {
    from { opacity: 0; transform: translateY(-4px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  /* Items in order */
  .vo-item {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 12px 18px;
    border-bottom: 1px solid var(--off-white);
  }

  .vo-item-icon {
    width: 36px; height: 36px;
    border-radius: 8px;
    background: var(--off-white);
    display: flex; align-items: center; justify-content: center;
    flex-shrink: 0;
    color: var(--ink-light);
    border: 1px solid var(--border);
  }

  .vo-item-name  { font-size: 14px; color: var(--ink); font-weight: 500; }
  .vo-item-meta  { font-size: 12px; color: var(--ink-light); margin-top: 2px; }
  .vo-item-price { font-size: 14px; font-weight: 600; color: var(--ink); margin-left: auto; white-space: nowrap; }

  /* Fulfill form */
  .vo-fulfill-form {
    padding: 16px 18px;
    border-top: 1px solid var(--border);
    background: var(--white);
  }

  .vo-fulfill-label {
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin-bottom: 10px;
    display: block;
  }

  .vo-fulfill-row { display: flex; gap: 10px; align-items: flex-end; }

  .vo-fulfill-fields { flex: 1; display: flex; gap: 10px; }

  .vo-input-group { flex: 1; }

  .vo-input-label {
    display: block;
    font-size: 11px;
    font-weight: 600;
    color: var(--ink-mid);
    margin-bottom: 5px;
  }

  .vo-input {
    width: 100%;
    background: var(--off-white);
    border: 1.5px solid var(--border);
    border-radius: 8px;
    padding: 9px 12px;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    color: var(--ink);
    outline: none;
    box-sizing: border-box;
    transition: border-color 0.15s;
  }

  .vo-input:focus { border-color: var(--teal); background: var(--white); }

  .vo-fulfill-btn {
    display: flex;
    align-items: center;
    gap: 7px;
    padding: 9px 18px;
    border-radius: 8px;
    border: none;
    background: var(--teal);
    color: white;
    font-family: 'IBM Plex Sans', sans-serif;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s;
  }

  .vo-fulfill-btn:hover { background: var(--teal-mid); }
  .vo-fulfill-btn:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Already shipped */
  .vo-tracking-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 12px 18px;
    background: var(--blue-bg);
    border-top: 1px solid var(--blue-border);
    font-size: 13px;
    color: var(--blue);
  }

  /* Customer info */
  .vo-customer-section {
    padding: 14px 18px;
    border-top: 1px solid var(--border);
    display: flex;
    gap: 24px;
    font-size: 13px;
    color: var(--ink-mid);
  }

  .vo-customer-field label {
    display: block;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: var(--ink-light);
    margin-bottom: 3px;
  }

  /* Empty state */
  .vo-empty {
    text-align: center;
    padding: 60px 24px;
    color: var(--ink-light);
  }
`;

// ─── Sub-order (per-vendor receipt) config ────────────────────────────────────

const STATUS_META = {
  pending:   { label: "Pending",   icon: Clock,       cls: "vo-fulfillment-unfulfilled" },
  shipped:   { label: "Shipped",   icon: Truck,       cls: "vo-fulfillment-shipped" },
  delivered: { label: "Delivered", icon: CheckCircle, cls: "vo-fulfillment-delivered" },
  completed: { label: "Completed", icon: CheckCircle, cls: "vo-fulfillment-delivered" },
  cancelled: { label: "Cancelled", icon: X,           cls: "vo-fulfillment-unfulfilled" },
  refunded:  { label: "Refunded",  icon: AlertCircle, cls: "vo-fulfillment-unfulfilled" },
};

const ITEM_ICONS = {
  physical: Package,
  digital:  Download,
  course:   BookOpen,
  session:  Calendar,
};

const formatDate = (iso) =>
  new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// ─── Order Card ───────────────────────────────────────────────────────────────

function OrderCard({ order, onUpdated }) {
  const [expanded, setExpanded]             = useState(order.status === "pending");
  const [trackingNumber, setTrackingNumber] = useState("");
  const [carrier, setCarrier]               = useState("USPS");
  const [submitting, setSubmitting]         = useState(false);
  const [error, setError]                   = useState("");

  const meta       = STATUS_META[order.status] || STATUS_META.pending;
  const StatusIcon = meta.icon;
  const needsShip  = order.requires_shipping;

  const addTracking = async () => {
    if (!trackingNumber.trim()) { setError("Enter a tracking number"); return; }
    setSubmitting(true); setError("");
    try {
      const updated = await apiFetch(`/vendor/orders/${order.id}/fulfill`, {
        method: "PATCH",
        body: JSON.stringify({ tracking_number: trackingNumber.trim(), carrier }),
      });
      onUpdated?.(updated);
      setTrackingNumber("");
    } catch (e) {
      setError(e.message || "Could not save tracking");
    } finally {
      setSubmitting(false);
    }
  };

  const setStatus = async (status) => {
    setSubmitting(true); setError("");
    try {
      const updated = await apiFetch(`/vendor/orders/${order.id}/status`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      onUpdated?.(updated);
    } catch (e) {
      setError(e.message || "Could not update status");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="vo-order-card" data-testid={`vendor-suborder-${order.order_number}`}>
      <div className="vo-order-head" onClick={() => setExpanded(v => !v)} data-testid="vendor-suborder-head">
        <span className="vo-order-ref" style={{ width: 130 }}>{order.order_number}</span>
        <span className="vo-order-customer">{order.buyer_name}</span>
        <span className="vo-order-date">{formatDate(order.created_at)}</span>
        <span className="vo-order-amount">${(order.subtotal || 0).toFixed(2)}</span>
        <span className={`vo-order-fulfillment ${meta.cls}`} data-testid="vendor-suborder-status">
          <StatusIcon size={10} /> {meta.label}
        </span>
        <button className="vo-expand-btn">
          {expanded ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
        </button>
      </div>

      {expanded && (
        <div className="vo-order-detail">
          {order.items.map((item, idx) => {
            const Icon = ITEM_ICONS[item.type] || Package;
            return (
              <div key={`${item.product_id}-${idx}`} className="vo-item">
                <div className="vo-item-icon"><Icon size={16} /></div>
                <div style={{ flex: 1 }}>
                  <div className="vo-item-name">{item.title}</div>
                  <div className="vo-item-meta">
                    {item.type}{item.variant_name ? ` · ${item.variant_name}` : ""} · qty {item.qty}
                  </div>
                </div>
                <span className="vo-item-price">${((item.price || 0) * item.qty).toFixed(2)}</span>
              </div>
            );
          })}

          {(order.tracking || []).map((t, i) => (
            <div className="vo-tracking-row" key={i} data-testid="vendor-suborder-tracking">
              <Truck size={14} />
              <span>{t.carrier} · <strong>{t.tracking_number}</strong></span>
              {t.url && (
                <a href={t.url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", color: "inherit" }}>
                  <ExternalLink size={12} />
                </a>
              )}
            </div>
          ))}

          {needsShip && ["pending", "shipped"].includes(order.status) && (
            <LabelPanel order={order} onUpdated={onUpdated} />
          )}

          {needsShip && ["pending", "shipped"].includes(order.status) && (
            <div className="vo-fulfill-form">
              <span className="vo-fulfill-label">
                {order.status === "pending" ? "Add tracking & mark shipped" : "Add another tracking number"}
              </span>
              <div className="vo-fulfill-row">
                <div className="vo-fulfill-fields">
                  <div className="vo-input-group">
                    <label className="vo-input-label">Tracking number</label>
                    <input
                      className="vo-input"
                      placeholder="9400111899223456789"
                      value={trackingNumber}
                      onChange={e => setTrackingNumber(e.target.value)}
                      data-testid="vendor-tracking-input"
                    />
                  </div>
                  <div className="vo-input-group" style={{ maxWidth: 130 }}>
                    <label className="vo-input-label">Carrier</label>
                    <select
                      className="vo-input"
                      style={{ appearance: "none", cursor: "pointer" }}
                      value={carrier}
                      onChange={e => setCarrier(e.target.value)}
                      data-testid="vendor-carrier-select"
                    >
                      <option>USPS</option>
                      <option>UPS</option>
                      <option>FedEx</option>
                      <option>DHL</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <button
                  className="vo-fulfill-btn"
                  onClick={addTracking}
                  disabled={submitting}
                  data-testid="vendor-save-tracking-btn"
                >
                  {submitting
                    ? <><Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> Saving...</>
                    : <><Truck size={13} /> {order.status === "pending" ? "Mark shipped" : "Add tracking"}</>}
                </button>
              </div>
              <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "10px 0 0" }}>
                You ship this order yourself. Tracking you add here appears on the buyer's receipt.
              </p>
              {error && (
                <p style={{ fontSize: 12, color: "var(--red)", margin: "8px 0 0" }} data-testid="vendor-order-error">
                  {error}
                </p>
              )}
            </div>
          )}

          {order.status === "shipped" && (
            <div className="vo-fulfill-form" style={{ borderTop: "none", paddingTop: 0 }}>
              <button
                className="vo-fulfill-btn"
                onClick={() => setStatus("delivered")}
                disabled={submitting}
                data-testid="vendor-mark-delivered-btn"
              >
                <Check size={13} /> Mark delivered
              </button>
            </div>
          )}

          {!needsShip && (
            <div className="vo-tracking-row" style={{ background: "var(--green-bg)", color: "var(--green)", borderTopColor: "var(--teal-border)" }}>
              <Check size={14} />
              <span>Digital order — delivered instantly, nothing to ship.</span>
            </div>
          )}

          <div className="vo-customer-section">
            <div className="vo-customer-field">
              <label>Customer</label>
              <span>{order.buyer_name}</span>
            </div>
            <div className="vo-customer-field">
              <label>Email</label>
              <span>{order.buyer_email || "—"}</span>
            </div>
            <div className="vo-customer-field">
              <label>Your payout</label>
              <span data-testid="vendor-suborder-payout">
                ${(order.vendor_payout || 0).toFixed(2)}{" "}
                <span style={{ color: "var(--ink-light)" }}>
                  (fee ${(order.platform_fee || 0).toFixed(2)}
                  {order.label?.cost ? ` · label $${order.label.cost.toFixed(2)}` : ""})
                </span>
              </span>
            </div>
            <div className="vo-customer-field">
              <label>Payout status</label>
              <span>{order.payout?.hold_reason || "Held until delivery"}</span>
            </div>
            <div className="vo-customer-field">
              <label>Payment ref</label>
              <span>{order.parent_order_number}</span>
            </div>
            {order.shipping_address && (
              <div className="vo-customer-field">
                <label>Ship to</label>
                <span>
                  {order.shipping_address.name}, {order.shipping_address.line1},{" "}
                  {order.shipping_address.city}, {order.shipping_address.state}{" "}
                  {order.shipping_address.zip}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Buy postage here (cost comes off this order's payout) ───────────────────

function LabelPanel({ order, onUpdated }) {
  const [rates, setRates]     = useState(null);
  const [shipmentId, setShipmentId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [buying, setBuying]   = useState(null);
  const [error, setError]     = useState("");

  if (order.label) {
    return (
      <div className="vo-tracking-row" style={{ background: "var(--green-bg)", color: "var(--green)" }} data-testid="vendor-label-bought">
        <Package size={14} />
        <span>
          {order.label.carrier} {order.label.service} label · ${order.label.cost?.toFixed(2)} taken off this payout
        </span>
        {order.label.label_url && (
          <a href={order.label.label_url} target="_blank" rel="noreferrer" style={{ marginLeft: "auto", color: "inherit", fontWeight: 600 }} data-testid="vendor-label-print">
            Print label <ExternalLink size={12} style={{ verticalAlign: "-1px" }} />
          </a>
        )}
      </div>
    );
  }

  const getRates = async () => {
    setLoading(true); setError("");
    try {
      const res = await apiFetch(`/vendor/shipping/rates/${order.id}`);
      setRates(res.rates || []);
      setShipmentId(res.shipment_id);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  const buy = async (rateId) => {
    setBuying(rateId); setError("");
    try {
      const res = await apiFetch(`/vendor/shipping/labels/${order.id}`, {
        method: "POST",
        body: JSON.stringify({ shipment_id: shipmentId, rate_id: rateId }),
      });
      onUpdated?.({ ...res.receipt, label: { ...res, bought_at: new Date().toISOString() } });
      if (res.label_url) window.open(res.label_url, "_blank");
    } catch (e) {
      setError(e.message);
    } finally {
      setBuying(null);
    }
  };

  return (
    <div className="vo-fulfill-form" data-testid="vendor-label-panel">
      <span className="vo-fulfill-label">Buy postage here (optional)</span>
      {!rates ? (
        <>
          <button className="vo-fulfill-btn" onClick={getRates} disabled={loading} data-testid="vendor-get-rates-btn">
            {loading
              ? <><Loader size={13} style={{ animation: "spin 1s linear infinite" }} /> Getting rates…</>
              : <><Truck size={13} /> Show shipping rates</>}
          </button>
          <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "10px 0 0" }}>
            Buy a label here and the cost comes straight off this order's payout — nothing to pay separately.
          </p>
        </>
      ) : rates.length === 0 ? (
        <p style={{ fontSize: 12.5, color: "var(--ink-light)", margin: 0 }}>No rates came back for this parcel.</p>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rates.slice(0, 6).map(r => (
            <div key={r.id} className="vo-item" style={{ borderRadius: 10 }} data-testid={`vendor-rate-${r.id}`}>
              <div style={{ flex: 1 }}>
                <div className="vo-item-name">{r.carrier} · {r.service}</div>
                <div className="vo-item-meta">
                  {r.delivery_days ? `${r.delivery_days} day(s)` : "Delivery estimate unavailable"}
                </div>
              </div>
              <span className="vo-item-price" style={{ marginRight: 10 }}>${r.rate.toFixed(2)}</span>
              <button
                className="vo-fulfill-btn"
                style={{ padding: "7px 14px" }}
                onClick={() => buy(r.id)}
                disabled={buying !== null}
                data-testid="vendor-buy-label-btn"
              >
                {buying === r.id ? "Buying…" : "Buy & print"}
              </button>
            </div>
          ))}
        </div>
      )}
      {error && <p style={{ fontSize: 12, color: "var(--red)", margin: "8px 0 0" }} data-testid="vendor-label-error">{error}</p>}
    </div>
  );
}

// ─── Payouts (held → releasable → paid) ──────────────────────────────────────

function PayoutsPanel() {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    apiFetch("/vendor/payouts")
      .then(setData)
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="vo-empty"><Loader size={20} style={{ animation: "spin 1s linear infinite" }} /></div>;
  if (!data || !data.payouts.length) {
    return (
      <div className="vo-empty" data-testid="vendor-payouts-empty">
        <Package size={30} style={{ opacity: 0.3, marginBottom: 12 }} />
        <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, color: "var(--ink)", margin: "0 0 6px" }}>No payouts yet</p>
        <p style={{ fontSize: 13, margin: 0 }}>Your earnings appear here as orders come in.</p>
      </div>
    );
  }

  const LABELS = {
    held:      "Held",
    eligible:  "Releasing",
    flagged:   "Under review",
    released:  "Paid",
    cancelled: "Cancelled",
  };

  return (
    <div data-testid="vendor-payouts-panel">
      <div className="vo-order-card" style={{ padding: 18, display: "flex", gap: 28, flexWrap: "wrap" }}>
        {[["Held", data.totals.held], ["Releasing", data.totals.eligible], ["Paid out", data.totals.released]].map(([label, value]) => (
          <div key={label}>
            <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".06em", color: "var(--ink-light)", fontWeight: 600 }}>{label}</div>
            <div style={{ fontFamily: "'Syne', sans-serif", fontSize: 22, color: "var(--teal)" }}>${value.toFixed(2)}</div>
          </div>
        ))}
        <p style={{ fontSize: 12, color: "var(--ink-light)", margin: 0, flexBasis: "100%", lineHeight: 1.55 }}>
          Your money is held safely by the platform until each order is delivered, then released to you.
          Shipping labels bought here and any refunds come off before it's sent — no separate bills, ever.
        </p>
      </div>

      {data.payouts.map(p => (
        <div className="vo-order-card" key={p.sub_order_id} data-testid={`vendor-payout-${p.order_number}`}>
          <div className="vo-order-head" style={{ cursor: "default" }}>
            <span className="vo-order-ref" style={{ width: 130 }}>{p.order_number}</span>
            <span className="vo-order-customer">{p.hold_reason}</span>
            <span className="vo-order-amount">${p.net.toFixed(2)}</span>
            <span
              className={`vo-order-fulfillment ${p.status === "released" ? "vo-fulfillment-delivered" : p.status === "held" ? "vo-fulfillment-unfulfilled" : "vo-fulfillment-shipped"}`}
              data-testid="vendor-payout-status"
            >
              {LABELS[p.status] || p.status}
            </span>
          </div>
          {(p.label_costs > 0 || p.refund_debits > 0 || p.other_debits > 0) && (
            <div className="vo-order-detail">
              <div className="vo-customer-section" style={{ borderTop: "none" }}>
                <div className="vo-customer-field"><label>Your share</label><span>${p.gross.toFixed(2)}</span></div>
                {p.label_costs > 0 && <div className="vo-customer-field"><label>Shipping labels</label><span>− ${p.label_costs.toFixed(2)}</span></div>}
                {p.refund_debits > 0 && <div className="vo-customer-field"><label>Refunds</label><span>− ${p.refund_debits.toFixed(2)}</span></div>}
                {p.other_debits > 0 && <div className="vo-customer-field"><label>Other costs</label><span>− ${p.other_debits.toFixed(2)}</span></div>}
                <div className="vo-customer-field"><label>You receive</label><span>${p.net.toFixed(2)}</span></div>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ─── Returns (vendor decides) ────────────────────────────────────────────────

function ReturnsPanel() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [notes, setNotes]       = useState({});
  const [escNotes, setEscNotes] = useState({});
  const [escError, setEscError] = useState({});
  const [busyId, setBusyId]     = useState(null);
  const [error, setError]       = useState("");

  const load = () => {
    apiFetch("/vendor/returns")
      .then(setRequests)
      .catch(() => setRequests([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, approve) => {
    setBusyId(id); setError("");
    try {
      await apiFetch(`/vendor/returns/${id}/decision`, {
        method: "PATCH",
        body: JSON.stringify({ approve, note: notes[id] || null }),
      });
      load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusyId(null);
    }
  };

  const escalate = async (id) => {
    if (!(escNotes[id] || "").trim()) {
      setEscError({ ...escError, [id]: "Tell us why you can't settle this first." });
      return;
    }
    setBusyId(id); setEscError({ ...escError, [id]: "" });
    try {
      await apiFetch(`/vendor/returns/${id}/escalate`, {
        method: "POST",
        body: JSON.stringify({ note: escNotes[id] }),
      });
      load();
    } catch (e) {
      setEscError({ ...escError, [id]: e.message });
    } finally {
      setBusyId(null);
    }
  };

  if (loading) return <div className="vo-empty"><Loader size={20} style={{ animation: "spin 1s linear infinite" }} /></div>;
  if (!requests.length) {
    return (
      <div className="vo-empty" data-testid="vendor-returns-empty">
        <AlertCircle size={30} style={{ opacity: 0.3, marginBottom: 12 }} />
        <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, color: "var(--ink)", margin: "0 0 6px" }}>
          No return requests
        </p>
        <p style={{ fontSize: 13, margin: 0 }}>When a buyer asks for a refund, it lands here.</p>
      </div>
    );
  }

  return (
    <>
      {requests.map(r => {
        const open = ["requested", "escalated"].includes(r.status);
        return (
          <div className="vo-order-card" key={r.id} data-testid={`vendor-return-${r.order_number}`}>
            <div className="vo-order-head" style={{ cursor: "default" }}>
              <span className="vo-order-ref" style={{ width: 130 }}>{r.order_number}</span>
              <span className="vo-order-customer">Refund requested: ${(r.amount || 0).toFixed(2)}</span>
              <span className="vo-order-date">{formatDate(r.created_at)}</span>
              <span
                className={`vo-order-fulfillment ${
                  r.status === "refunded" ? "vo-fulfillment-delivered"
                  : r.status === "declined" ? "vo-fulfillment-unfulfilled"
                  : "vo-fulfillment-shipped"}`}
                data-testid="vendor-return-status"
              >
                {r.status}
              </span>
            </div>
            <div className="vo-order-detail">
              <div className="vo-customer-section" style={{ borderTop: "none" }}>
                {r.issue_label && (
                  <div className="vo-customer-field">
                    <label>What went wrong</label>
                    <span data-testid="vendor-return-issue">
                      {r.issue_label} · they asked for a {r.resolution === "replacement" ? "replacement" : "refund"}
                    </span>
                  </div>
                )}
                <div className="vo-customer-field">
                  <label>Buyer&apos;s reason</label>
                  <span>{r.reason}</span>
                </div>
                {(r.evidence || []).length > 0 && (
                  <div className="vo-customer-field">
                    <label>Photos</label>
                    <span style={{ display: "flex", gap: 6, flexWrap: "wrap" }} data-testid="vendor-return-photos">
                      {(r.evidence || []).map((url, i) => (
                        <a key={`${url}-${i}`} href={url} target="_blank" rel="noreferrer">
                          <img
                            src={url}
                            alt={`Buyer photo ${i + 1}`}
                            style={{ width: 54, height: 54, objectFit: "cover", borderRadius: 8, border: "1px solid var(--border)" }}
                          />
                        </a>
                      ))}
                    </span>
                  </div>
                )}
                {r.vendor_note && (
                  <div className="vo-customer-field">
                    <label>Your note</label>
                    <span>{r.vendor_note}</span>
                  </div>
                )}
                {r.admin_note && (
                  <div className="vo-customer-field">
                    <label>Admin note</label>
                    <span>{r.admin_note}</span>
                  </div>
                )}
              </div>

              {r.outside_policy && (
                <div className="vo-tracking-row" data-testid="vendor-return-outside-policy">
                  <AlertCircle size={14} />
                  <span>
                    This falls outside your published policy{r.policy_block ? ` — ${r.policy_block}` : ""} It&apos;s
                    your call: you&apos;re not obliged to accept it.
                  </span>
                </div>
              )}

              {r.status === "escalated" && (
                <div className="vo-tracking-row" style={{ background: "var(--amber-bg)", color: "var(--amber)", borderTopColor: "var(--amber-border)" }}>
                  <AlertCircle size={14} />
                  <span>
                    {r.escalated_by === "vendor"
                      ? "You asked our team to review this. We'll make the final call and the buyer has been told."
                      : "The buyer asked our team to review this. An admin may make the final call."}
                  </span>
                </div>
              )}

              {open && (
                <div className="vo-fulfill-form">
                  <span className="vo-fulfill-label">Approve or decline (a reason is required to decline)</span>
                  <div className="vo-fulfill-row">
                    <div className="vo-fulfill-fields">
                      <div className="vo-input-group">
                        <input
                          className="vo-input"
                          placeholder="Add a note for the buyer..."
                          value={notes[r.id] || ""}
                          onChange={e => setNotes({ ...notes, [r.id]: e.target.value })}
                          data-testid="vendor-return-note"
                        />
                      </div>
                    </div>
                    <button
                      className="vo-fulfill-btn"
                      onClick={() => decide(r.id, true)}
                      disabled={busyId === r.id}
                      data-testid="vendor-return-approve-btn"
                    >
                      <Check size={13} /> Approve refund
                    </button>
                    <button
                      className="vo-fulfill-btn"
                      style={{ background: "var(--red)" }}
                      onClick={() => decide(r.id, false)}
                      disabled={busyId === r.id}
                      data-testid="vendor-return-decline-btn"
                    >
                      <X size={13} /> Decline
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "10px 0 0" }}>
                    Approving refunds the buyer straight away and our team is notified either way. You lose your
                    share of the sale, but the Stripe processing fee on a refund is absorbed by us, not charged to you.
                  </p>
                  {error && <p style={{ fontSize: 12, color: "var(--red)", margin: "8px 0 0" }} data-testid="vendor-return-error">{error}</p>}
                </div>
              )}

              {r.vendor_can_escalate && (
                <div className="vo-fulfill-form" data-testid="vendor-return-escalate-form">
                  <span className="vo-fulfill-label">Can&apos;t settle it?</span>
                  <p style={{ fontSize: 11, color: "var(--ink-light)", margin: "4px 0 10px" }}>
                    Tell us why and our team will rule on it instead. Our decision is final for both sides, and the
                    buyer is told it&apos;s with us.
                  </p>
                  <div className="vo-fulfill-row">
                    <div className="vo-fulfill-fields">
                      <div className="vo-input-group">
                        <input
                          className="vo-input"
                          placeholder="Why you can't settle this..."
                          value={escNotes[r.id] || ""}
                          onChange={e => setEscNotes({ ...escNotes, [r.id]: e.target.value })}
                          data-testid="vendor-return-escalate-note"
                        />
                      </div>
                    </div>
                    <button
                      className="vo-fulfill-btn"
                      style={{ background: "#b85828" }}
                      onClick={() => escalate(r.id)}
                      disabled={busyId === r.id}
                      data-testid="vendor-return-escalate-btn"
                    >
                      <AlertCircle size={13} /> Ask our team to decide
                    </button>
                  </div>
                  {escError[r.id] && (
                    <p style={{ fontSize: 12, color: "var(--red)", margin: "8px 0 0" }} data-testid="vendor-escalate-error">
                      {escError[r.id]}
                    </p>
                  )}
                </div>
              )}

              {r.execution && (
                <div className="vo-customer-section">
                  <div className="vo-customer-field">
                    <label>Refunded to buyer</label>
                    <span>${(r.execution.buyer_refund ?? r.amount ?? 0).toFixed(2)}</span>
                  </div>
                  {r.execution.restocking_fee > 0 && (
                    <div className="vo-customer-field">
                      <label>Restocking fee you kept</label>
                      <span>${r.execution.restocking_fee.toFixed(2)}</span>
                    </div>
                  )}
                  <div className="vo-customer-field">
                    <label>Off your payout</label>
                    <span>${(r.execution.reversal_amount || 0).toFixed(2)}</span>
                  </div>
                  <div className="vo-customer-field">
                    <label>
                      {r.execution.stripe_fee_absorbed !== undefined
                        ? "Stripe fee (absorbed by us)"
                        : "Stripe fee you carried"}
                    </label>
                    <span data-testid="refund-stripe-fee-absorbed">
                      ${(r.execution.stripe_fee_absorbed ?? r.execution.vendor_stripe_fee ?? 0).toFixed(2)}
                    </span>
                  </div>
                  <div className="vo-customer-field">
                    <label>Platform fee returned</label>
                    <span>${(r.execution.platform_fee_returned || 0).toFixed(2)}</span>
                  </div>
                  {r.return_label?.label_url && (
                    <div className="vo-customer-field">
                      <label>Return label ({r.return_label.paid_by === "vendor" ? "you pay" : "buyer pays"})</label>
                      <span>
                        <a href={r.return_label.label_url} target="_blank" rel="noreferrer" style={{ color: "inherit" }} data-testid="vendor-return-label-link">
                          {r.return_label.carrier} {r.return_label.service} · ${r.return_label.cost?.toFixed(2)}
                        </a>
                      </span>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function VendorOrders() {
  const [orders, setOrders] = useState([]);
  const [tab, setTab]       = useState("pending");
  const [search, setSearch] = useState("");
  const [page, setPage]     = useState(1);
  const [loading, setLoading] = useState(true);
  const PER_PAGE            = 50;

  useEffect(() => { setPage(1); }, [tab, search]);

  const load = () => {
    apiFetch("/vendor/orders?per_page=200")
      .then(resp => setOrders(resp?.orders || []))
      .catch(() => setOrders([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleUpdated = (updated) => {
    setOrders(prev => prev.map(o => (o.id === updated.id ? updated : o)));
  };

  const pendingCount = orders.filter(o => o.status === "pending").length;

  const filtered = orders.filter(o => {
    const matchTab =
      tab === "all"       ? true :
      tab === "pending"   ? o.status === "pending" :
      tab === "shipped"   ? o.status === "shipped" :
      tab === "delivered" ? ["delivered", "completed"].includes(o.status) : true;

    const q = search.toLowerCase();
    const matchSearch =
      search === "" ||
      (o.order_number || "").toLowerCase().includes(q) ||
      (o.buyer_name || "").toLowerCase().includes(q) ||
      (o.parent_order_number || "").toLowerCase().includes(q);

    return matchTab && matchSearch;
  });

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <>
      <style>{styles}</style>
      <div className="vo-root" data-testid="vendor-orders-page">
        <div className="vo-header">
          <h1>Orders</h1>
          <div className="vo-search">
            <Search size={14} color="var(--ink-light)" />
            <input
              placeholder="Search by order # or customer..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              data-testid="vendor-orders-search"
            />
          </div>
        </div>

        <div className="vo-tabs">
          {[
            { id: "pending",   label: "Needs shipping", badge: pendingCount },
            { id: "shipped",   label: "Shipped",        badge: null },
            { id: "delivered", label: "Completed",      badge: null },
            { id: "all",       label: "All orders",     badge: null },
            { id: "returns",   label: "Returns",        badge: null },
            { id: "payouts",   label: "Payouts",        badge: null },
          ].map(t => (
            <button
              key={t.id}
              className={`vo-tab ${tab === t.id ? "active" : ""}`}
              onClick={() => setTab(t.id)}
              data-testid={`vendor-orders-tab-${t.id}`}
            >
              {t.label}
              {t.badge > 0 && <span className="vo-tab-badge">{t.badge}</span>}
            </button>
          ))}
        </div>

        <div className="vo-list">
          {tab === "returns" ? (
            <ReturnsPanel />
          ) : tab === "payouts" ? (
            <PayoutsPanel />
          ) : loading ? (
            <div className="vo-empty"><Loader size={22} style={{ animation: "spin 1s linear infinite" }} /></div>
          ) : filtered.length === 0 ? (
            <div className="vo-empty" data-testid="vendor-orders-empty">
              <Package size={32} style={{ opacity: 0.3, marginBottom: 12 }} />
              <p style={{ fontFamily: "'Syne', sans-serif", fontSize: 17, color: "var(--ink)", margin: "0 0 6px" }}>
                {tab === "pending" ? "All caught up" : "No orders found"}
              </p>
              <p style={{ fontSize: 13, margin: 0 }}>
                {tab === "pending" ? "No orders waiting to be shipped." : "Try changing your filter."}
              </p>
            </div>
          ) : (
            pageItems.map(o => (
              <OrderCard key={o.id} order={o} onUpdated={handleUpdated} />
            ))
          )}
        </div>
        {!["returns", "payouts"].includes(tab) && (
          <Pagination page={page} totalPages={totalPages} onChange={setPage} testidPrefix="vendor-orders-pagination" />
        )}
      </div>
    </>
  );
}
